/**
 * The device PIN (spec 15.3): six digits, PBKDF2-SHA256 through Web Crypto with a random
 * salt, stored as a hash in `sesi_lokal`. It is a lock, not encryption: it decides whether
 * the app opens, nothing about what is on disk. Five wrong attempts impose a one-minute
 * cooldown, kept in the same row so a reload does not reset it.
 *
 * Pure apart from `crypto.subtle`, so the tests run it under Node.
 */

export const PIN_LENGTH = 6
export const PIN_MAX_FAILURES = 5
export const PIN_COOLDOWN_MS = 60_000
/**
 * Spec 15.3: the PIN is asked again after this long without input (./kunciDiam). A sign-in that
 * waits for an account step ends after as long (./session).
 */
export const IDLE_LOCK_MS = 10 * 60_000
export const PBKDF2_ITERATIONS = 100_000
const SALT_BYTES = 16
const HASH_BYTES = 32

export function isValidPin(pin: string): boolean {
  return new RegExp(`^\\d{${PIN_LENGTH}}$`).test(pin)
}

function toBase64(bytes: Uint8Array): string {
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary)
}

function fromBase64(text: string): Uint8Array<ArrayBuffer> {
  const binary = atob(text)
  const bytes = new Uint8Array(new ArrayBuffer(binary.length))
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes
}

export function makeSalt(): string {
  const bytes = new Uint8Array(SALT_BYTES)
  crypto.getRandomValues(bytes)
  return toBase64(bytes)
}

export async function hashPin(
  pin: string,
  salt: string,
  iterations = PBKDF2_ITERATIONS,
): Promise<string> {
  const key = await crypto.subtle.importKey('raw', new TextEncoder().encode(pin), 'PBKDF2', false, [
    'deriveBits',
  ])
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', hash: 'SHA-256', salt: fromBase64(salt), iterations },
    key,
    HASH_BYTES * 8,
  )
  return toBase64(new Uint8Array(bits))
}

/** Compares the whole hash regardless of where the first difference is. */
export async function verifyPin(
  pin: string,
  salt: string,
  hash: string,
  iterations = PBKDF2_ITERATIONS,
): Promise<boolean> {
  const candidate = await hashPin(pin, salt, iterations)
  if (candidate.length !== hash.length) return false
  let diff = 0
  for (let i = 0; i < hash.length; i++) diff |= candidate.charCodeAt(i) ^ hash.charCodeAt(i)
  return diff === 0
}

/** The failure counter and the cooldown as stored in `sesi_lokal`. */
export interface PinLockState {
  gagal: number
  /** ISO timestamp; attempts are refused until then. */
  terkunciSampai: string | null
}

export const PIN_UNLOCKED: PinLockState = { gagal: 0, terkunciSampai: null }

/** Epoch ms until which the PIN is locked, or null when attempts are allowed. */
export function pinLockedUntil(state: PinLockState, now: number): number | null {
  if (state.terkunciSampai === null) return null
  const until = Date.parse(state.terkunciSampai)
  if (Number.isNaN(until) || until <= now) return null
  return until
}

export type PinOutcome =
  | { ok: true; next: PinLockState }
  | { ok: false; alasan: 'salah' | 'terkunci'; next: PinLockState }

/**
 * The state after one attempt. A correct PIN clears the counter; the fifth wrong PIN in
 * a row starts the cooldown and resets the counter, so the minute is served once.
 */
export function nextPinState(state: PinLockState, correct: boolean, now: number): PinOutcome {
  if (pinLockedUntil(state, now) !== null) return { ok: false, alasan: 'terkunci', next: state }
  // An expired cooldown counts from zero again.
  const gagal = state.terkunciSampai === null ? state.gagal : 0
  if (correct) return { ok: true, next: PIN_UNLOCKED }
  const failures = gagal + 1
  if (failures >= PIN_MAX_FAILURES) {
    return {
      ok: false,
      alasan: 'terkunci',
      next: { gagal: 0, terkunciSampai: new Date(now + PIN_COOLDOWN_MS).toISOString() },
    }
  }
  return { ok: false, alasan: 'salah', next: { gagal: failures, terkunciSampai: null } }
}
